/**
 * Selector pago único / cuotas con selling plans (Seal Subscriptions).
 * Reutiliza la UI spp2__* e inyecta `selling_plan` en el form de add to cart.
 */
(function () {
  const WIDGET_SELECTOR = '[data-offsider-selling-plan-widget]';
  const SELLING_PLAN_ATTR = 'data-offsider-selling-plan-field';

  /**
   * @returns {HTMLFormElement | null}
   */
  function getProductAddToCartForm() {
    const form = document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getWidget() {
    const widget = document.querySelector(WIDGET_SELECTOR);
    return widget instanceof HTMLElement ? widget : null;
  }

  /**
   * @returns {Record<string, number | null>}
   */
  function getVariantPlanMap() {
    const widget = getWidget();
    if (!widget) {
      return {};
    }

    try {
      const raw = widget.dataset.variantSellingPlans || '{}';
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @returns {string | null}
   */
  function getCurrentVariantId() {
    const form = getProductAddToCartForm();
    const variantInput = form?.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement && variantInput.value ? variantInput.value : null;
  }

  /**
   * @returns {string | null}
   */
  function getSellingPlanIdForCurrentVariant() {
    const widget = getWidget();
    if (!widget) {
      return null;
    }

    const variantId = getCurrentVariantId();
    const map = getVariantPlanMap();

    if (variantId && Object.prototype.hasOwnProperty.call(map, variantId)) {
      const mapped = map[variantId];
      return mapped != null ? String(mapped) : null;
    }

    const fallback = widget.dataset.sellingPlanId;
    return fallback || null;
  }

  /**
   * @returns {boolean}
   */
  function isInstallmentPaymentSelected() {
    const widget = getWidget();
    if (!widget) {
      return false;
    }

    const selected = widget.querySelector('input[name="payment-option"]:checked');
    return selected instanceof HTMLInputElement && selected.value === '1';
  }

  /**
   * @param {HTMLInputElement} input
   */
  function activatePaymentOption(input) {
    if (!input.checked) {
      input.click();
      return;
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Sincroniza el campo selling_plan al formulario de add to cart.
   */
  function syncSellingPlanToBuyForm() {
    const productForm = getProductAddToCartForm();
    const widget = getWidget();

    if (!productForm || !widget) {
      return;
    }

    for (const existing of productForm.querySelectorAll(`[${SELLING_PLAN_ATTR}]`)) {
      existing.remove();
    }

    if (!isInstallmentPaymentSelected()) {
      return;
    }

    const sellingPlanId = getSellingPlanIdForCurrentVariant();
    if (!sellingPlanId) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = sellingPlanId;
    input.setAttribute(SELLING_PLAN_ATTR, 'true');
    productForm.appendChild(input);
  }

  function prepareBeforeAddToCart() {
    syncSellingPlanToBuyForm();
  }

  /**
   * @param {HTMLElement} content
   * @param {HTMLInputElement} input
   * @param {HTMLElement} label
   */
  function bindPaymentOptionRow(content, input, label) {
    if (content.dataset.spp2ClickBound === 'true') {
      return;
    }

    content.dataset.spp2ClickBound = 'true';

    content.addEventListener('click', (event) => {
      if (event.target === input) {
        return;
      }

      if (label.contains(/** @type {Node} */ (event.target))) {
        return;
      }

      activatePaymentOption(input);
      syncSellingPlanToBuyForm();
    });
  }

  /**
   * @param {HTMLElement} widget
   */
  function enhanceWidget(widget) {
    for (const option of widget.querySelectorAll('.spp2__widget-option')) {
      const input = option.querySelector('input[type="radio"]');
      const content = option.querySelector('.spp2__payment-option-content');
      const label = content?.querySelector('.spp2__payment-option-label');
      const radio = content?.querySelector('.spp2__radio');

      if (!(input instanceof HTMLInputElement) || !content || !label || !radio) {
        continue;
      }

      bindPaymentOptionRow(content, input, label);
    }
  }

  function scanWidgets() {
    const widget = getWidget();
    if (widget) {
      enhanceWidget(widget);
      syncSellingPlanToBuyForm();
    }
  }

  function bindIntegration() {
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) {
          return;
        }

        if (target.name !== 'payment-option' || !target.closest(WIDGET_SELECTOR)) {
          return;
        }

        window.requestAnimationFrame(() => {
          syncSellingPlanToBuyForm();
        });
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
          return;
        }

        if (form.getAttribute('data-type') !== 'add-to-cart-form') {
          return;
        }

        prepareBeforeAddToCart();
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }

        const button = target.closest(
          'product-form-component button[type="submit"], product-form-component add-to-cart-component button'
        );

        if (!button) {
          return;
        }

        prepareBeforeAddToCart();
      },
      true
    );

    document.addEventListener('variant:update', () => {
      window.requestAnimationFrame(() => {
        syncSellingPlanToBuyForm();
      });
    });
  }

  window.OffsiderSellingPlanPayment = {
    isInstallmentPaymentSelected,
    prepareBeforeAddToCart,
    syncSellingPlanToBuyForm,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanWidgets, { once: true });
  } else {
    scanWidgets();
  }

  bindIntegration();

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(scanWidgets);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
